import AsyncSelectWithServer from '@/components/Form/Shared/AsyncSelectWithServer'
import { useModal } from '@/contexts/modal'
import { CreateCompanyProps } from '@/modals/CompanyModals/CreateCompany'
import { getCompaniesOptions } from '@/modules/Company/services'
import { convertCustomer } from '@/modules/Customer/services'
import { ModalProps } from '@/types/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Dialog, GridHookForm } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface ConvertCustomerProps extends ModalProps {
    customer_id: number
    customer_name: string
}

interface ConvertCustomerFormValues {
    company_id: number
}

const ConvertCustomer: React.FC<ConvertCustomerProps> = ({
    onClose,
    open,
    customer_id,
    customer_name,
}) => {
    const formControl = useForm<ConvertCustomerFormValues>({
        defaultValues: {
            company_id: 0,
        },
    })

    const { t } = useTranslation()
    const { setModal } = useModal()
    const queryClient = useQueryClient()

    const { mutateAsync: convert, isPending } = useMutationWithHandler(
        { mutationFn: convertCustomer },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('convert_customer_success'),
            onSuccess: () => {
                queryClient.refetchQueries({
                    queryKey: [QUERY_KEYS.CUSTOMER, customer_id.toString()],
                })
                onClose()
            },
        }
    )

    const handleNoOptionCompany = (value: string) => {
        setModal<CreateCompanyProps>('CompanyModals/CreateCompany/index', {
            input: value,
            onSuccessCreateCompany: (data) => {
                convert({
                    company_id: data?.result?.company_id,
                    customer_id: customer_id,
                })
            },
        })
    }

    return (
        <Dialog onClose={onClose} open={open} title={t('convert_customer')}>
            <Alert variant="info">
                {t('convert_customer_description', {
                    replace: {
                        customer_name,
                    },
                })}
            </Alert>
            <GridHookForm
                className="mt-4"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        disabled: isPending,
                        handler: onClose,
                    },
                    {
                        title: t('save'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
                data={[
                    {
                        renderEditComponent: (control) => {
                            return (
                                <AsyncSelectWithServer
                                    placeholder={`${t('company_tax_code')} *`}
                                    control={control}
                                    asyncOptions={getCompaniesOptions}
                                    name="company_id"
                                    label={`${t('company_tax_code')} *`}
                                    noOptionHandler={handleNoOptionCompany}
                                />
                            )
                        },
                        className: 'md:col-span-4 col-span-1',
                    },
                ]}
                onSubmit={(values) =>
                    convert({ company_id: values?.company_id, customer_id })
                }
            />
        </Dialog>
    )
}

export default ConvertCustomer
